import { useEffect, useMemo, useState } from 'react'
import debounce from 'lodash.debounce'
import {
  Autocomplete,
  Button,
  Card,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useGetVendorsLiveList } from '@/services/hooks/relay/live-lists'
import { VendorLiveListEntity } from '@/types/live-lists'
import { useInfiniteScrollListbox } from '@/components/validation/fields/InfiniteScrollListBox'

const VENDOR_FIELDS = 'entity_id,pd_display_name,external_vendor_id'

// Stable reference so the controlled Autocomplete `value` never changes
// identity between renders. Selections are surfaced as rows below the field,
// not as tags, so the picker itself is always empty.
const EMPTY_SELECTION: VendorLiveListEntity[] = []

type ExcludedVendorsFieldProps = {
  orgId: string
  orgCredentialTypeId: string
  value: string
  onChange: (value: string) => void
}

function parseNumbers(value: string): string[] {
  return value ? value.split(',') : []
}

export default function ExcludedVendorsField({
  orgId,
  orgCredentialTypeId,
  value,
  onChange,
}: ExcludedVendorsFieldProps) {
  const [open, setOpen] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [searchValue, setSearchValue] = useState('')
  // Vendors selected this session, keyed by external_vendor_id. The
  // `external_vendor_id__in` hydration query below is re-keyed by the full
  // excluded-numbers list on every add/remove, so it always refetches from
  // scratch — without this, a just-added vendor wouldn't render until that
  // round-trip completes. We already have the full vendor object from the
  // dropdown selection, so use it immediately and let the hydration query
  // (below) confirm/replace it once it resolves.
  const [locallyKnownVendors, setLocallyKnownVendors] = useState<
    Record<string, VendorLiveListEntity>
  >({})

  const excludedNumbers = useMemo(() => parseNumbers(value), [value])

  // Dropdown suggestions: server-ordered, server-filtered by `query`, paged on
  // scroll. Only fetched once the dropdown is open.
  const {
    allData: vendors,
    hasNextPage,
    fetchNextPage,
    isFetching: isFetchingVendors,
  } = useGetVendorsLiveList({
    orgId,
    orgCredentialTypeId,
    filters: {
      query: searchValue,
      fields__only: VENDOR_FIELDS,
      limit: '20',
    },
    enabled: open,
    placeholderData: (previousData) => previousData,
  })

  // Hydration: resolve the stored vendor numbers to display names via the bulk
  // `external_vendor_id__in` filter. Fetch exactly as many as are stored.
  const { allData: hydratedVendors } = useGetVendorsLiveList({
    orgId,
    orgCredentialTypeId,
    filters: {
      external_vendor_id__in: excludedNumbers.join(','),
      fields__only: VENDOR_FIELDS,
      limit: excludedNumbers.length.toString(),
    },
    enabled: excludedNumbers.length > 0,
    placeholderData: (previousData) => previousData,
  })

  // Server data wins once it's in (it's the canonical source); the locally
  // known vendor fills the gap until then.
  const vendorByNumber = useMemo(() => {
    const map = new Map<string, VendorLiveListEntity>()
    Object.values(locallyKnownVendors).forEach((vendor) => {
      if (vendor.external_vendor_id != null) {
        map.set(vendor.external_vendor_id, vendor)
      }
    })
    hydratedVendors.forEach((vendor) => {
      if (vendor.external_vendor_id != null) {
        map.set(vendor.external_vendor_id, vendor)
      }
    })
    return map
  }, [hydratedVendors, locallyKnownVendors])

  const excludedVendorRows = useMemo(
    () =>
      excludedNumbers
        .map((vendorNumber) => vendorByNumber.get(vendorNumber))
        .filter((vendor): vendor is VendorLiveListEntity => vendor != null)
        .sort((a, b) =>
          (a.pd_display_name || '').localeCompare(b.pd_display_name || ''),
        ),
    [excludedNumbers, vendorByNumber],
  )

  const debouncedSearch = useMemo(
    () => debounce((newValue: string) => setSearchValue(newValue), 200),
    [],
  )

  useEffect(() => {
    return () => {
      debouncedSearch.cancel()
    }
  }, [debouncedSearch])

  const { listboxSlotProps } = useInfiniteScrollListbox({
    hasNextPage,
    fetchNextPage,
    isFetching: isFetchingVendors,
    optionsLength: vendors.length,
  })

  const addVendor = (vendor: VendorLiveListEntity) => {
    const vendorNumber = vendor.external_vendor_id
    if (!vendorNumber || excludedNumbers.includes(vendorNumber)) return
    setLocallyKnownVendors((prev) => ({ ...prev, [vendorNumber]: vendor }))
    onChange([...excludedNumbers, vendorNumber].join(','))
  }

  const removeVendorNumber = (vendorNumber: string) => {
    onChange(excludedNumbers.filter((num) => num !== vendorNumber).join(','))
  }

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2">Excluded Vendors</Typography>
      <Typography variant="caption" color="text.secondary">
        PO match will be skipped for invoices from these vendors.
      </Typography>

      <Autocomplete<VendorLiveListEntity, true, false, false>
        multiple
        autoHighlight
        options={vendors}
        value={EMPTY_SELECTION}
        open={open}
        inputValue={inputValue}
        loading={isFetchingVendors}
        filterOptions={(options) => options}
        getOptionLabel={(option) => option.pd_display_name || ''}
        isOptionEqualToValue={(option, selected) =>
          option.entity_id === selected.entity_id
        }
        getOptionDisabled={(option) =>
          option.external_vendor_id == null ||
          excludedNumbers.includes(option.external_vendor_id)
        }
        onOpen={() => {
          setOpen(true)
          debouncedSearch.cancel()
          setSearchValue('')
          setInputValue('')
        }}
        onClose={() => {
          setOpen(false)
          debouncedSearch.cancel()
          setSearchValue('')
          setInputValue('')
        }}
        onInputChange={(_e, newInputValue, reason) => {
          if (reason === 'input') {
            setInputValue(newInputValue)
            debouncedSearch(newInputValue)
          } else {
            setInputValue('')
            debouncedSearch.cancel()
            setSearchValue('')
          }
        }}
        onChange={(_e, _value, reason, details) => {
          if (reason === 'selectOption' && details?.option) {
            addVendor(details.option)
          }
        }}
        renderTags={() => null}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Add vendor"
            placeholder="Search vendors"
            slotProps={{ inputLabel: { shrink: true } }}
          />
        )}
        renderOption={(props, option) => {
          const missingNumber = option.external_vendor_id == null
          return (
            <MenuItem {...props} key={option.entity_id}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2">
                  {option.pd_display_name}
                </Typography>
                {missingNumber && (
                  <Typography variant="caption" color="text.disabled">
                    (missing vendor number)
                  </Typography>
                )}
              </Stack>
            </MenuItem>
          )
        }}
        slotProps={{ listbox: listboxSlotProps }}
      />

      {excludedVendorRows.length > 0 && (
        <Card variant="outlined" sx={{ mt: 1 }}>
          {excludedVendorRows.map((vendor, index) => (
            <Stack key={vendor.external_vendor_id}>
              {index > 0 && <Divider />}
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ px: 2, py: 1 }}
              >
                <Typography variant="body2">
                  {vendor.pd_display_name}
                </Typography>
                <Button
                  size="small"
                  color="error"
                  variant="text"
                  aria-label={`Remove ${vendor.pd_display_name}`}
                  onClick={() =>
                    removeVendorNumber(vendor.external_vendor_id as string)
                  }
                >
                  Remove
                </Button>
              </Stack>
            </Stack>
          ))}
        </Card>
      )}
    </Stack>
  )
}
